import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PickedImage } from "../User/adding-user/ImageUploadContainer";

/**
 * SCRUM-2219: the Add and Update Ad form state, written as a createSlice like
 * every reducer in this client (SCRUM-1954), but mounted LOCALLY with
 * `useReducer(adForm.reducer, addFormState())` / `updateFormState(ad)` — it is
 * the open panel's unsaved draft, not store data (the SCRUM-2127 pattern).
 *
 * The two screens differ in one way, kept exactly as they behaved before:
 * Update writes the picked client / product id and the picked image INTO the
 * ad it saves (and drops the key when "None" is picked), while Add keeps them
 * beside the ad and composes the payload when it saves. `mode` says which.
 */

/** A react-select option for a client or product; `label` is missing on a stored ref until the list loads. */
export type AdOption = { value: string; label?: string; countryCode?: string };

/** The ad being edited. Add starts with the four text fields only; Update with the stored row. */
export type AdDraft = {
  _id?: string;
  adsTitle: string;
  adsDescription: string;
  adsLink: string;
  /** Update only: a stored URL, or the picker's object ({ data, imgName } / { secure_url }). */
  adsMedia?: string | PickedImage;
  adsPosition: string;
  client?: string | null;
  product?: string | null;
};

/** The ad row ManageAds hands to the Update screen (a lean ad from GET /api/ads). */
export type AdRow = {
  _id?: string;
  adsTitle?: string;
  adsDescription?: string;
  adsLink?: string;
  adsMedia?: string;
  adsPosition?: string;
  client?: string;
  product?: string;
};

export type AdFormState = {
  mode: "add" | "update";
  ad: AdDraft;
  client: AdOption | null;
  product: AdOption | null;
  /** Add only: the picked image, saved as `adsMedia` by the container. */
  media: PickedImage | null;
  /** the picked product's country, which names the banner upload folder */
  countryCode: string | null;
  loading: boolean;
};

const emptyAd = (): AdDraft => ({ adsTitle: "", adsDescription: "", adsLink: "", adsPosition: "" });

const addFormInitial: AdFormState = {
  mode: "add",
  ad: emptyAd(),
  client: null,
  product: null,
  media: null,
  countryCode: null,
  loading: false,
};

/** The Add screen's empty form. */
export const addFormState = (): AdFormState => ({ ...addFormInitial, ad: emptyAd() });

const adFromRow = (row?: AdRow) => ({
  _id: row?._id || "",
  adsTitle: row?.adsTitle || "",
  adsDescription: row?.adsDescription || "",
  adsLink: row?.adsLink || "",
  adsMedia: row?.adsMedia || "",
  adsPosition: row?.adsPosition || "",
  client: row?.client || null,
  product: row?.product || null,
});

/** The Update screen's form, filled from the row being edited. */
export const updateFormState = (row?: AdRow): AdFormState => ({
  ...addFormInitial,
  mode: "update",
  ad: adFromRow(row),
  client: row?.client ? { value: row.client } : null,
  product: row?.product ? { value: row.product } : null,
});

type StoreRecord = { _id: string; country_code?: string };

const withNone = (options: AdOption[]): AdOption[] => [
  ...options,
  { value: "", label: "None", countryCode: "" },
];

/** Store products → select options plus "None"; `[]` while the list has not loaded. */
export const productOptions = (products: unknown): AdOption[] =>
  Array.isArray(products)
    ? withNone(
        (products as (StoreRecord & { title?: string })[]).map((p) => ({
          value: p._id,
          label: p.title,
          countryCode: p.country_code,
        }))
      )
    : [];

/** Store clients → select options plus "None"; `[]` while the list has not loaded. */
export const clientOptions = (clients: unknown): AdOption[] =>
  Array.isArray(clients)
    ? withNone(
        (clients as (StoreRecord & { name?: string })[]).map((c) => ({
          value: c._id,
          label: c.name,
          countryCode: c.country_code,
        }))
      )
    : [];

const isNone = (opt: AdOption | null) => !opt || opt.value === "";

export const adForm = createSlice({
  name: "adForm",
  initialState: addFormInitial,
  reducers: {
    /** Update only: ManageAds re-feeds the mounted screen when another row's Edit is clicked. */
    adLoaded(state, action: PayloadAction<AdRow>) {
      const row = action.payload;
      Object.assign(state.ad, adFromRow(row));
      state.client = row.client ? { value: row.client } : null;
      state.product = row.product ? { value: row.product } : null;
    },

    fieldChanged(state, action: PayloadAction<{ name: string; value: string }>) {
      (state.ad as Record<string, unknown>)[action.payload.name] = action.payload.value;
    },

    positionSelected(state, action: PayloadAction<AdOption | null>) {
      state.ad.adsPosition = action.payload ? action.payload.value : "";
    },

    clientSelected(state, action: PayloadAction<AdOption | null>) {
      const opt = action.payload;
      if (state.mode === "add") {
        state.client = opt;
        return;
      }
      if (isNone(opt)) {
        delete state.ad.client;
        state.client = null;
        return;
      }
      state.ad.client = opt!.value;
      state.client = opt;
    },

    productSelected(state, action: PayloadAction<AdOption | null>) {
      const opt = action.payload;
      if (state.mode === "add") {
        state.product = opt;
        state.countryCode = opt ? (opt.countryCode ?? null) : null;
        return;
      }
      if (isNone(opt)) {
        delete state.ad.product;
        state.product = null;
        state.countryCode = null;
        return;
      }
      state.ad.product = opt!.value;
      state.product = opt;
      state.countryCode = opt!.countryCode ?? null;
    },

    imagePicked(state, action: PayloadAction<PickedImage>) {
      if (state.mode === "add") state.media = action.payload;
      else state.ad.adsMedia = action.payload;
    },

    uploadStarted(state) {
      state.loading = true;
    },

    /** An upload that threw, or the server's errors arriving in the store. */
    saveFailed(state) {
      state.loading = false;
    },

    /** The store's success message arrived: Add starts over, Update keeps the saved values. */
    saved(state) {
      if (state.mode === "add") return addFormState();
      state.loading = false;
    },
  },
});
